import React from 'react';
import { UserCog, CheckCircle, ArrowRight, AlertTriangle, Users, Shield } from 'lucide-react';
import ModernDocHeader from '../../components/documentation/ModernDocHeader';

const UserManagementDocs: React.FC = () => {
  return (
    <div>
      <ModernDocHeader
        title="User Management"
        description="Add team members, manage access permissions, and organize your sales team for efficient collaboration and lead management."
        icon={<UserCog className="w-6 h-6" />}
        badge="Essential"
        badgeColor="purple"
      />

      <div className="p-8">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-8 max-w-4xl">
          <div className="prose prose-blue dark:prose-invert max-w-none">

      {/* What You'll Learn */}
      <div className="not-prose bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
        <div className="flex items-start space-x-3">
          <Users className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-200 mb-3">
              What You'll Learn
            </h3>
            <ul className="space-y-1 text-sm text-blue-800 dark:text-blue-300">
              <li>✓ Adding and inviting team members</li>
              <li>✓ Understanding user roles and permissions</li>
              <li>✓ Managing user status and access</li>
              <li>✓ Organizing teams and departments</li>
              <li>✓ Handling password resets and security</li>
              <li>✓ Best practices for user management</li>
            </ul>
          </div>
        </div>
      </div>

      <h2>Overview</h2>
      <p>
        User Management allows you to build and organize your sales team within the CRM. Add team members,
        assign appropriate access levels, and manage their permissions to ensure data security while enabling
        efficient collaboration.
      </p>

      <h2>Adding New Users</h2>

      <h3>Step 1: Navigate to User Management</h3>
      <p>Go to <code>Settings → Users</code></p>

      <h3>Step 2: Create New User</h3>
      <ol>
        <li>Click "Add New User" button</li>
        <li>Fill in user details:
          <ul>
            <li><strong>Full Name:</strong> User's complete name</li>
            <li><strong>Email:</strong> Must be unique, used for login</li>
            <li><strong>Phone:</strong> Contact number (optional)</li>
            <li><strong>Role:</strong> Select from Super Admin, Admin, or Sales Rep</li>
            <li><strong>Team/Department:</strong> Assign to specific team (optional)</li>
            <li><strong>Status:</strong> Active or Inactive</li>
          </ul>
        </li>
        <li>Click "Create User"</li>
        <li>System generates temporary password and sends email invite</li>
      </ol>

      <div className="not-prose bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 my-4">
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
          <strong>💡 Email Invitation:</strong>
        </p>
        <p className="text-sm text-gray-700 dark:text-gray-300">
          New users receive an email with login credentials and a link to set their password.
          They must change the temporary password on first login.
        </p>
      </div>

      <h2>User Roles Explained</h2>

      <div className="not-prose space-y-3 my-6">
        <div className="p-4 border-l-4 border-red-400 dark:border-red-600 bg-red-50 dark:bg-red-900/20 rounded-lg">
          <div className="flex items-start space-x-3">
            <Shield className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="font-semibold text-red-900 dark:text-red-200 mb-2">Super Admin</h4>
              <p className="text-sm text-red-700 dark:text-red-300 mb-2">Complete system access and control</p>
              <ul className="text-xs text-red-600 dark:text-red-400 space-y-1">
                <li>• View and manage ALL leads (across all users)</li>
                <li>• Create, edit, delete users</li>
                <li>• Modify system settings and integrations</li>
                <li>• Access billing and subscription</li>
                <li>• View all reports and analytics</li>
                <li>• Configure Round Robin and drip campaigns</li>
                <li>• Manage API keys and webhooks</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="p-4 border-l-4 border-blue-400 dark:border-blue-600 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
          <div className="flex items-start space-x-3">
            <Shield className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="font-semibold text-blue-900 dark:text-blue-200 mb-2">Admin</h4>
              <p className="text-sm text-blue-700 dark:text-blue-300 mb-2">Team management and oversight</p>
              <ul className="text-xs text-blue-600 dark:text-blue-400 space-y-1">
                <li>• View ALL leads (read-only for others' leads)</li>
                <li>• Create and edit users (cannot delete)</li>
                <li>• Configure some settings (not billing)</li>
                <li>• View team reports and analytics</li>
                <li>• Manage pipeline and drip campaigns</li>
                <li>• Cannot access billing or delete accounts</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="p-4 border-l-4 border-green-400 dark:border-green-600 bg-green-50 dark:bg-green-900/20 rounded-lg">
          <div className="flex items-start space-x-3">
            <Shield className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="font-semibold text-green-900 dark:text-green-200 mb-2">Sales Representative</h4>
              <p className="text-sm text-green-700 dark:text-green-300 mb-2">Individual contributor with limited access</p>
              <ul className="text-xs text-green-600 dark:text-green-400 space-y-1">
                <li>• View and edit ONLY assigned leads</li>
                <li>• Add remarks, log calls, schedule follow-ups</li>
                <li>• Move leads through pipeline stages</li>
                <li>• Update lead details and custom fields</li>
                <li>• View personal performance reports</li>
                <li>• Cannot see other reps' leads</li>
                <li>• No access to settings or user management</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <h2>Managing Existing Users</h2>

      <h3>Editing User Information</h3>
      <ol>
        <li>Go to Users list</li>
        <li>Click on user name or edit icon</li>
        <li>Update details (name, phone, role, team)</li>
        <li>Click "Save Changes"</li>
      </ol>

      <h3>Changing User Role</h3>
      <div className="not-prose bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 my-6">
        <div className="flex items-start space-x-3">
          <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm text-yellow-800 dark:text-yellow-300 mb-2">
              <strong>⚠️ Important:</strong>
            </p>
            <p className="text-sm text-yellow-700 dark:text-yellow-300">
              When demoting from Admin to Sales Rep, user loses access to all leads except those assigned to them.
              When promoting to Admin, user gains access to all leads immediately.
            </p>
          </div>
        </div>
      </div>

      <h3>Deactivating vs Deleting Users</h3>
      <div className="not-prose grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
        <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
          <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Deactivate (Recommended)</h4>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            Temporarily disable access without losing data
          </p>
          <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
            <li>• User cannot log in</li>
            <li>• Leads remain assigned to them</li>
            <li>• Activity history preserved</li>
            <li>• Can be reactivated later</li>
            <li>• Use for: Vacation, leave, suspension</li>
          </ul>
        </div>

        <div className="p-4 border border-red-200 dark:border-red-700 bg-red-50 dark:bg-red-900/10 rounded-lg">
          <h4 className="font-semibold text-red-900 dark:text-red-200 mb-2">Delete (Permanent)</h4>
          <p className="text-sm text-red-700 dark:text-red-300 mb-2">
            Permanently remove user and reassign leads
          </p>
          <ul className="text-sm text-red-600 dark:text-red-400 space-y-1">
            <li>• Cannot be undone!</li>
            <li>• Must reassign their leads first</li>
            <li>• Activity logs remain (anonymized)</li>
            <li>• Email freed for reuse</li>
            <li>• Use for: Terminated employees only</li>
          </ul>
        </div>
      </div>

      <h2>Password Management</h2>

      <h3>User Forgot Password</h3>
      <ol>
        <li>User clicks "Forgot Password" on login page</li>
        <li>Enters email address</li>
        <li>Receives password reset link via email</li>
        <li>Creates new password (must meet requirements)</li>
      </ol>

      <h3>Admin Reset User Password</h3>
      <ol>
        <li>Go to Users → Select user</li>
        <li>Click "Reset Password"</li>
        <li>System generates temporary password</li>
        <li>User receives email with reset link</li>
        <li>User must change password on next login</li>
      </ol>

      <h3>Password Requirements</h3>
      <ul>
        <li>Minimum 8 characters</li>
        <li>At least one uppercase letter</li>
        <li>At least one lowercase letter</li>
        <li>At least one number</li>
        <li>At least one special character (@, #, $, etc.)</li>
      </ul>

      <h2>Teams & Organization</h2>

      <h3>Creating Teams</h3>
      <p>Organize users into teams or departments:</p>
      <ol>
        <li>Go to <code>Settings → Teams</code></li>
        <li>Click "Create New Team"</li>
        <li>Enter team name (e.g., "Inside Sales", "Field Sales", "Support")</li>
        <li>Add team members</li>
        <li>Assign team leader (optional)</li>
      </ol>

      <h3>Benefits of Teams</h3>
      <ul>
        <li><strong>Organize Reports:</strong> View performance by team</li>
        <li><strong>Round Robin:</strong> Create team-specific lead assignment rules</li>
        <li><strong>Collaboration:</strong> Team members can share leads within team</li>
        <li><strong>Leaderboards:</strong> Compare team performance</li>
      </ul>

      <h2>User Activity Monitoring</h2>

      <h3>Activity Logs</h3>
      <p>Track what users are doing in the system:</p>
      <ul>
        <li>Login/logout times</li>
        <li>Leads created or modified</li>
        <li>Calls made and logged</li>
        <li>Emails sent</li>
        <li>Settings changes</li>
        <li>Export/import actions</li>
      </ul>

      <h3>Performance Metrics</h3>
      <p>View user performance from <code>Reports → Team Performance</code>:</p>
      <div className="not-prose">
        <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden my-6">
          <table className="w-full text-sm">
            <thead className="bg-gray-100 dark:bg-gray-700">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white">Metric</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white">Description</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              <tr>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300 font-medium">Leads Assigned</td>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300">Total leads currently assigned</td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300 font-medium">Conversion Rate</td>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300">Percentage of leads converted to Won</td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300 font-medium">Response Time</td>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300">Average time to first contact</td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300 font-medium">Activities Logged</td>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300">Calls, emails, meetings logged</td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300 font-medium">Revenue Generated</td>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300">Total value of Won deals</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <h2>Security Best Practices</h2>

      <div className="not-prose bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6 my-8">
        <div className="flex items-start space-x-3">
          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-lg font-semibold text-green-900 dark:text-green-200 mb-3">
              User Security Checklist
            </h3>
            <ul className="space-y-2 text-sm text-green-800 dark:text-green-300">
              <li>✓ <strong>Principle of least privilege</strong> - Give minimum access needed for job role</li>
              <li>✓ <strong>Regular audits</strong> - Review user list quarterly, remove inactive accounts</li>
              <li>✓ <strong>Strong passwords</strong> - Enforce password requirements, no sharing</li>
              <li>✓ <strong>Immediate deactivation</strong> - Disable access same day employee leaves</li>
              <li>✓ <strong>Monitor activity</strong> - Review logs for suspicious behavior</li>
              <li>✓ <strong>Unique emails</strong> - One email per user, no shared accounts</li>
              <li>✓ <strong>Two-factor authentication</strong> - Enable for Admin and Super Admin roles</li>
            </ul>
          </div>
        </div>
      </div>

      <h2>Common User Management Tasks</h2>

      <h3>Onboarding New Sales Rep</h3>
      <ol>
        <li>Create user with Sales Rep role</li>
        <li>Assign to appropriate team</li>
        <li>Add to Round Robin group with limited capacity initially</li>
        <li>Share training documentation and credentials</li>
        <li>Schedule onboarding session to walk through CRM</li>
        <li>Assign a few test leads for practice</li>
        <li>Increase Round Robin capacity after training period</li>
      </ol>

      <h3>Handling Employee Departure</h3>
      <ol>
        <li>Immediately deactivate user account</li>
        <li>Remove from all Round Robin groups</li>
        <li>Reassign active leads to other team members</li>
        <li>Export their activity history (if needed)</li>
        <li>After transition complete, delete user if permanent</li>
      </ol>

      <h3>Managing Seasonal Team Members</h3>
      <ul>
        <li>Create accounts when they join</li>
        <li>Set up as Sales Rep with limited access</li>
        <li>Deactivate (don't delete) at end of season</li>
        <li>Reactivate when they return next season</li>
        <li>Previous leads and history preserved</li>
      </ul>

      <h2>Troubleshooting</h2>

      <div className="not-prose bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-200 dark:divide-gray-700 my-4">
        <details className="p-4">
          <summary className="font-medium text-gray-900 dark:text-white cursor-pointer">
            User not receiving invitation email?
          </summary>
          <div className="mt-2 text-sm text-gray-600 dark:text-gray-400 space-y-2">
            <p><strong>Check:</strong></p>
            <ul className="list-disc list-inside space-y-1">
              <li>Email address is spelled correctly</li>
              <li>Check spam/junk folder</li>
              <li>Verify email server is working (send test email)</li>
              <li>Resend invitation from user settings</li>
              <li>Manually share login credentials if email fails</li>
            </ul>
          </div>
        </details>

        <details className="p-4">
          <summary className="font-medium text-gray-900 dark:text-white cursor-pointer">
            Can't delete user - "User has assigned leads"?
          </summary>
          <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            <p><strong>Solution:</strong></p>
            <ol className="list-decimal list-inside space-y-1 mt-2">
              <li>Go to user's profile → View Assigned Leads</li>
              <li>Select all leads</li>
              <li>Use bulk action "Reassign To" → select new owner</li>
              <li>Once all leads reassigned, you can delete user</li>
            </ol>
          </div>
        </details>

        <details className="p-4">
          <summary className="font-medium text-gray-900 dark:text-white cursor-pointer">
            User says they can't see certain leads?
          </summary>
          <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            <p><strong>Check role and permissions:</strong></p>
            <ul className="list-disc list-inside space-y-1 mt-2">
              <li>Sales Reps only see assigned leads - this is intentional</li>
              <li>If they should see all leads, promote to Admin role</li>
              <li>Check if leads are assigned to someone else</li>
              <li>Verify user account is Active, not deactivated</li>
            </ul>
          </div>
        </details>
      </div>

      <h2>Next Steps</h2>

      <div className="not-prose space-y-3 mt-6">
        <a
          href="/docs/roles-permissions"
          className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-500 dark:hover:border-blue-500 transition-colors"
        >
          <div>
            <p className="font-medium text-gray-900 dark:text-white">Roles & Permissions</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Deep dive into access control and permissions
            </p>
          </div>
          <ArrowRight className="w-5 h-5 text-gray-400" />
        </a>

        <a
          href="/docs/round-robin"
          className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-500 dark:hover:border-blue-500 transition-colors"
        >
          <div>
            <p className="font-medium text-gray-900 dark:text-white">Round Robin Assignment</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Auto-distribute leads among team members
            </p>
          </div>
          <ArrowRight className="w-5 h-5 text-gray-400" />
        </a>

        <a
          href="/docs/pipeline"
          className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-500 dark:hover:border-blue-500 transition-colors"
        >
          <div>
            <p className="font-medium text-gray-900 dark:text-white">Pipeline Management</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Track team performance through pipeline stages
            </p>
          </div>
          <ArrowRight className="w-5 h-5 text-gray-400" />
        </a>
      </div>

      <div className="not-prose bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6 mt-8">
        <div className="flex items-start space-x-3">
          <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-lg font-semibold text-green-900 dark:text-green-200 mb-2">
              Team Management Ready!
            </h3>
            <p className="text-green-800 dark:text-green-300 mb-3">
              You now understand how to build and manage your sales team in the CRM. Remember:
              assign appropriate roles, monitor activity, and regularly audit user access.
            </p>
            <a
              href="/settings/users"
              className="inline-flex items-center px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors"
            >
              <UserCog className="w-4 h-4 mr-2" />
              Manage Users
            </a>
          </div>
        </div>
      </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default UserManagementDocs;
